import _ from 'lodash';
import fp from 'lodash/fp';
import pino from 'pino';
import serializeError from 'serialize-error';
import { isProd, isStaging } from './environment';
import { shouldReport } from './error-with-code';
import { captureError } from './sentry-utils';

const enableLogging = !process.env.DISABLE_LOGGER;

class PleaseLogger {
  public pinoInstance: pino.Logger;
  public context: {};

  constructor(options = {}) {
    const loggerOptions = {
      base: getBaseOptions(),
      level: process.env.LOGGER_LEVEL || 'trace',
      enabled: enableLogging,
      ...options,
    };
    this.pinoInstance = pino(loggerOptions);
    // default value for context
    this.context = {};
  }

  public setContext(context: {}) {
    this.context = context;
  }

  public trace(...args: any[]) {
    const processedArgs = mergeArgsWithContext(this.context, args);
    if (processedArgs.length > 1) {
      // @ts-ignore
      this.pinoInstance.trace(...processedArgs);
    }
  }

  public debug(...args: any[]) {
    const processedArgs = mergeArgsWithContext(this.context, args);
    if (processedArgs.length > 0) {
      // @ts-ignore
      this.pinoInstance.debug(...processedArgs);
    }
  }

  public info(...args: any[]) {
    const processedArgs = mergeArgsWithContext(this.context, args);
    if (processedArgs.length > 0) {
      // @ts-ignore
      this.pinoInstance.info(...processedArgs);
    }
  }

  public warn(...args: any[]) {
    const processedArgs = mergeArgsWithContext(this.context, args);
    if (processedArgs.length > 0) {
      // @ts-ignore
      this.pinoInstance.warn(...processedArgs);
    }
  }

  public error(...args: any[]) {
    const processedArgs = mergeArgsWithContext(this.context, args);
    if (processedArgs.length > 0) {
      // @ts-ignore
      this.pinoInstance.error(...processedArgs);
    }
  }

  public fatal(...args: any[]) {
    const processedArgs = mergeArgsWithContext(this.context, args);
    if (processedArgs.length > 0) {
      // @ts-ignore
      this.pinoInstance.fatal(...processedArgs);
    }
  }
}

function extractPropertiesFromContext(context: any) {
  return { awsRequestId: context.awsRequestId };
}

function mergeArgsWithContext(context: {}, args: any[]) {
  let mergeObject: any = extractPropertiesFromContext(context);

  const [firstArg, ...restArgs] = args;
  let stringMsg;

  if (_.isString(firstArg)) {
    stringMsg = firstArg;
  } else if (_.isError(firstArg)) {
    const error = firstArg;
    if (shouldReport(error)) {
      captureError(error);
    }
    mergeObject = {
      ...mergeObject,
      error: stringifyError(error),
    };
  } else if (_.isObject(firstArg)) {
    mergeObject = {
      ...mergeObject,
      ...firstArg,
    };
    mergeObject = _.mapValues(mergeObject, value => {
      if (_.isError(value)) {
        const error = value;
        if (shouldReport(error)) {
          captureError(error);
        }
        if (error) return stringifyError(error);
      }
      return value;
    });
  }
  return [mergeObject, stringMsg, ...restArgs].filter(v => !!v);
}

function stringifyError(error: Error) {
  if (error.constructor.name === 'MongooseError') {
    return JSON.stringify(error);
  }
  return serializeError(error);
}

export function getBaseOptions() {
  const functionDetails = getFunctionDetails() || {};
  return {
    ...functionDetails,
    environment: process.env.NODE_ENV,
    release: process.env.GIT_COMMIT_LONG,
  };
}

/**
 * From the Full Function name provided by AWS Lambda in the environment variable
 * it identifies the name of the micro service & the function name
 * @example
 * // if process.env.AWS_LAMBDA_FUNCTION_NAME = 'serverless-wishlist-staging-get'
 * getFunctionDetails(); // returns { microService: 'wishlist', functionName: 'get' }
 *
 * @returns {undefined|{microService: string, functionName: string}}
 */
export function getFunctionDetails() {
  const fullFunctionName = process.env.AWS_LAMBDA_FUNCTION_NAME;
  if (!fullFunctionName) return undefined;

  let splits;
  if (isProd) {
    splits = fullFunctionName.split('production');
  } else if (isStaging) {
    splits = fullFunctionName.split('staging');
  }
  if (!splits || splits.length !== 2) return undefined;

  const microService = fp.pipe(
    fp.replace(/^serverless-/, ''),
    fp.trimCharsEnd('-'),
  )(splits[0]);
  const functionName = _.trimStart(splits[1], '-');
  return { microService, functionName };
}

let loggerInstance: PleaseLogger | undefined;
export function getLoggerInstance(options = {}) {
  if (!loggerInstance) {
    loggerInstance = new PleaseLogger(options);
  }
  return loggerInstance;
}

export { getLoggerInstance as instance };
